import React from 'react';
import {
  useCardTemplatesQuery,
  useCardTemplateMemberQuery,
} from '@trello/graphql/generated';
import { Spinner } from 'app/src/components/Spinner';
import { Button, Tooltip } from '@trello/nachos';
import { CardContents } from 'app/gamma/src/components/canonical-components';
import { CardLink } from '@atlassian/trello-canonical-components/src/card-front';
import { normalizeDataForCanonicalCard } from './normalizeDataForCanonicalCard';
import { forTemplate } from 'app/common/lib/util/i18n';
import RouterLink from 'app/common/components/singlespa/router-link';
import { TemplateCardComposer } from './TemplateCardComposer';
import { useShortcut, Scope, Key } from '@trello/keybindings';
import styles from './CardTemplatesPopover.less';
import { IconButton } from 'app/common/components/controls/button/button';
import { IconName, IconColor } from 'app/common/components/controls';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { TrelloStorage } from '@trello/storage';

const format = forTemplate('card-templates');

interface CardTemplatesPopoverProps {
  idBoard: string;
  idList: string;
  onSelect: (idCard: string) => void;
  onTemplateCreated: (card: { id: string; shortLink: string }) => void;
  confirmDelete: (idCard: string) => void;
  editTemplate?: (idCard: string) => void;
  isEditing: boolean;
  setIsEditing: (val: boolean) => void;
}

export const CardTemplatesPopover: React.FunctionComponent<CardTemplatesPopoverProps> = ({
  idBoard,
  idList,
  onSelect,
  onTemplateCreated,
  confirmDelete,
  editTemplate,
  isEditing,
  setIsEditing,
}) => {
  useShortcut(
    () => {
      setIsEditing(false);
    },
    {
      scope: Scope.Popover,
      key: Key.Escape,
      enabled: isEditing,
    },
  );

  const { data, loading, error } = useCardTemplatesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      boardId: idBoard,
    },
  });

  const {
    data: memberData,
    loading: memberLoading,
  } = useCardTemplateMemberQuery({
    fetchPolicy: 'cache-and-network',
  });

  const cards = data && data.board && data.board.cards ? data.board.cards : [];
  const templateCards = cards
    .filter((card) => card && card.isTemplate)
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
        caseFirst: 'upper',
      }),
    );

  const boardMembers =
    data && data.board && data.board.members ? data.board.members : [];

  if (error) {
    return <div id="cardTemplatesError">{format('something-went-wrong')}</div>;
  }

  const isLoading = loading || memberLoading;

  if (!data && isLoading) {
    return <Spinner centered />;
  }

  if (templateCards.length === 0) {
    return (
      <TemplateCardComposer
        idList={idList}
        idBoard={idBoard}
        onCreate={onTemplateCreated}
        closedComponent={(onClick) => {
          return (
            <div>
              <p className={styles.emptyStateMessage}>
                {format('you-dont-have-any-templates')}
              </p>
              <Button
                onClick={onClick}
                className={styles.createNewTemplateButton}
                appearance="primary"
                shouldFitContainer
              >
                {format('create-a-new-template')}
              </Button>
            </div>
          );
        }}
      />
    );
  }

  const labelState: {
    showText: boolean;
  } = TrelloStorage.get('labelState') || {
    showText: false,
  };

  const colorBlind = memberData?.member?.prefs?.colorBlind;

  return (
    <>
      <div className={styles.templateCards}>
        {templateCards.map((card) => {
          const {
            cover,
            badges,
            labels,
            members,
            stickers,
          } = normalizeDataForCanonicalCard(card, boardMembers);

          return (
            <div className={styles.templateCardContainer} key={card.id}>
              <CardLink
                className={styles.templateCard}
                onClick={() => onSelect(card.id)}
                cover={cover}
                colorBlind={colorBlind}
              >
                <CardContents
                  name={card.name}
                  cover={cover}
                  badges={badges}
                  isTemplate={card.isTemplate}
                  labels={labels}
                  expandLabels={labelState.showText}
                  members={members}
                  stickers={stickers}
                  colorBlind={colorBlind}
                />
              </CardLink>
              {isEditing && (
                <div className={styles.controlButtons}>
                  <Tooltip content={format('edit-this-template')}>
                    <RouterLink
                      className={styles.noTextDecoration}
                      onClick={() => {
                        trackUe({
                          category: Noun.CARD_TEMPLATES,
                          verb: Verb.OPENS,
                          directObj: Noun.CARD_TEMPLATE,
                          indirectObj: Noun.CARD_TEMPLATES_MENU,
                          method: 'by clicking the edit this template button',
                        });
                        editTemplate?.(card.id);
                      }}
                      href={`/c/${card.shortLink}`}
                    >
                      <IconButton
                        icon={IconName.Edit}
                        color={IconColor.Gray}
                        smallIcon
                        className={styles.controlButton}
                      />
                    </RouterLink>
                  </Tooltip>
                  <Tooltip content={format('delete-this-template')}>
                    <IconButton
                      onClick={() => confirmDelete(card.id)}
                      icon={IconName.Trash}
                      color={IconColor.Gray}
                      smallIcon
                      className={styles.controlButton}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <TemplateCardComposer
        idList={idList}
        idBoard={idBoard}
        onCreate={onTemplateCreated}
      />
      {isEditing ? (
        <Button
          className={styles.finishEditingButton}
          onClick={() => setIsEditing(false)}
          shouldFitContainer
        >
          {format('finish-editing')}
        </Button>
      ) : (
        <Button
          className={styles.editTemplatesButton}
          onClick={() => setIsEditing(true)}
          shouldFitContainer
        >
          {format('edit-templates')}
        </Button>
      )}
    </>
  );
};
